import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { SEOHead } from "@/components/SEOHead";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";

const SIZES = [
  { id: "small", label: "Small (6\")", desc: "Serves 6–8 · ₹1,200" },
  { id: "medium", label: "Medium (8\")", desc: "Serves 12–16 · ₹2,000" },
  { id: "large", label: "Large (10\")", desc: "Serves 20–25 · ₹3,200" },
  { id: "tiered", label: "Tiered (custom)", desc: "Serves 30+ · From ₹5,500" },
];

const FLAVORS = ["Vanilla bean", "Dark chocolate", "Lemon olive oil", "Red velvet", "Pistachio rose", "Tiramisu"];
const FROSTINGS = ["Swiss buttercream", "Italian meringue", "Cream cheese", "Whipped mascarpone", "Dark ganache"];

export default function CustomCake() {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [date, setDate] = useState();
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    cake_size: "medium",
    flavor: FLAVORS[0],
    frosting: FROSTINGS[0],
    inscription: "",
    special_requests: "",
    reference_image: "",
  });

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error("Please select a delivery date");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/custom-cakes", { ...form, delivery_date: date.toISOString() });
      setDone(r.data);
      toast.success("Cake request received! We'll be in touch within 24 hours.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="custom-cake-success">
        <p className="eyebrow mb-3" style={{ color: "var(--accent)" }}>Request received</p>
        <h1 className="font-display text-5xl mb-6">Let's bake something beautiful.</h1>
        <p className="text-lg leading-relaxed" style={{ color: "var(--ink-soft)" }}>
          Thank you, {done.customer_name}. Our pastry team will email you at <strong>{done.customer_email}</strong> within 24 hours
          to confirm details and pricing.
        </p>
        <p className="font-mono text-sm mt-8" style={{ color: "var(--ink-soft)" }}>Ref: {done.id}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="custom-cake-page">
      <SEOHead
        title="Order a Custom Cake in Mumbai | Cocoamaya Patisserie"
        description="Custom-made cakes for birthdays, weddings and anniversaries. Tell us your dream and we'll bake it. Minimum 72 hours notice."
        keywords="custom cake mumbai, birthday cake customisation, wedding cake mumbai, anniversary cake mumbai, personalised cakes, bespoke cakes andheri, cocoamaya custom cake"
        url="https://cocoamayapatisserie.com/custom-cake"
      />
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
        <aside className="md:col-span-5 md:sticky md:top-28 md:self-start">
          <p className="eyebrow mb-3" style={{ color: "var(--accent)" }}>Custom · Made to order</p>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight leading-none">
            Cakes for the<br/>moments<br/><span className="italic">that matter.</span>
          </h1>
          <p className="mt-6 text-lg leading-relaxed" style={{ color: "var(--ink-soft)" }}>
            Birthdays, weddings, anniversaries — tell us your dream and we'll bake it. Minimum 72 hours notice.
          </p>
          <img src="https://images.pexels.com/photos/28954461/pexels-photo-28954461.jpeg" alt="Custom cake" className="mt-8 w-full aspect-[4/5] object-cover rounded-sm" />
        </aside>

        <form onSubmit={submit} className="md:col-span-7 space-y-10">
          <section>
            <p className="eyebrow mb-5">01 · Your details</p>
            <div className="space-y-4">
              <input className="input-field" placeholder="Full name" required value={form.customer_name} onChange={(e) => onChange("customer_name", e.target.value)} data-testid="cc-name" />
              <input className="input-field" type="email" placeholder="Email" required value={form.customer_email} onChange={(e) => onChange("customer_email", e.target.value)} data-testid="cc-email" />
              <input className="input-field" placeholder="Phone" required value={form.customer_phone} onChange={(e) => onChange("customer_phone", e.target.value)} data-testid="cc-phone" />
            </div>
          </section>

          <section>
            <p className="eyebrow mb-5">02 · Choose a size</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SIZES.map((s) => (
                <label key={s.id}
                  className="p-5 border cursor-pointer transition-colors"
                  style={{
                    borderColor: form.cake_size === s.id ? "var(--ink)" : "var(--line)",
                    background: form.cake_size === s.id ? "var(--bg-soft)" : "transparent",
                  }}
                  data-testid={`size-${s.id}`}
                >
                  <input type="radio" name="size" checked={form.cake_size === s.id} onChange={() => onChange("cake_size", s.id)} className="sr-only" />
                  <p className="font-medium">{s.label}</p>
                  <p className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>{s.desc}</p>
                </label>
              ))}
            </div>
          </section>

          <section>
            <p className="eyebrow mb-5">03 · Flavor & frosting</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <select className="input-field" value={form.flavor} onChange={(e) => onChange("flavor", e.target.value)} data-testid="cc-flavor">
                {FLAVORS.map((f) => <option key={f}>{f}</option>)}
              </select>
              <select className="input-field" value={form.frosting} onChange={(e) => onChange("frosting", e.target.value)} data-testid="cc-frosting">
                {FROSTINGS.map((f) => <option key={f}>{f}</option>)}
              </select>
            </div>
          </section>

          <section>
            <p className="eyebrow mb-5">04 · Inscription & date</p>
            <div className="space-y-4">
              <input className="input-field" placeholder='Inscription (e.g., "Happy 30th, Mia")' value={form.inscription} onChange={(e) => onChange("inscription", e.target.value)} data-testid="cc-inscription" />
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="input-field text-left flex items-center gap-3" data-testid="cc-date-picker">
                    <CalendarIcon size={16} strokeWidth={1.5} />
                    {date ? format(date, "PPP") : <span style={{ color: "var(--ink-soft)" }}>Pick a delivery date</span>}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(Date.now() + 72 * 3600 * 1000)} initialFocus />
                </PopoverContent>
              </Popover>
              <input className="input-field" placeholder="Reference image URL (optional)" value={form.reference_image} onChange={(e) => onChange("reference_image", e.target.value)} data-testid="cc-image" />
              <textarea className="input-field" rows={4} placeholder="Special requests, dietary needs, design notes…" value={form.special_requests} onChange={(e) => onChange("special_requests", e.target.value)} data-testid="cc-requests" />
            </div>
          </section>

          <button type="submit" className="btn-primary w-full justify-center" disabled={submitting} data-testid="cc-submit-btn">
            {submitting ? "Submitting…" : "Request a quote"}
          </button>
        </form>
      </div>
    </div>
  );
}
